import {
  AppBar,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useState } from 'react';

type SimplOndemandAction = {
  id: string;
  title: string;
  description: string;
  accentColor: string;
  borderColor: string;
};

const actions: SimplOndemandAction[] = [
  {
    id: 'simpl-ondemand-provision',
    title: 'Simpl deployment',
    description: 'Prepare and initialize Simpl OnDemand resources.',
    accentColor: '#0b3a78',
    borderColor: '#bfdbfe',
  },
  {
    id: 'simpl-ondemand-sync',
    title: 'Simpl build',
    description: 'Synchronize configuration and templates.',
    accentColor: '#0b3a78',
    borderColor: '#bfdbfe',
  },
  {
    id: 'simpl-ondemand-cleanup',
    title: 'Simpl env',
    description: 'Remove unused resources and stale state.',
    accentColor: '#0b3a78',
    borderColor: '#bfdbfe',
  },
];

type DeliveryTarget = {
  id: string;
  label: string;
};

export const SimplOndemandPage = () => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [targets, setTargets] = useState<DeliveryTarget[]>([]);
  const [selectedTarget, setSelectedTarget] = useState('');
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const deliveryFeatureEnabled =
    configApi.getOptionalBoolean('deliveryCredentials.enabled') ?? false;

  const loadTargets = async () => {
    setLoadingTargets(true);
    setError('');

    try {
      const { token } = await identityApi.getCredentials();
      const response = await fetch('/api/delivery/targets', {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(payload.error ?? 'Failed to load targets');
      }

      const payload = (await response.json()) as { targets?: DeliveryTarget[] };
      setTargets(payload.targets ?? []);
    } catch (downloadError) {
      setError(
        downloadError instanceof Error
          ? downloadError.message
          : 'Failed to load delivery targets',
      );
    } finally {
      setLoadingTargets(false);
    }
  };

  const openDialog = async () => {
    setDialogOpen(true);
    if (targets.length === 0) {
      await loadTargets();
    }
  };

  const handleDownload = async () => {
    if (!selectedTarget) {
      return;
    }

    setDownloading(true);
    setError('');

    try {
      const { token } = await identityApi.getCredentials();
      const response = await fetch('/api/delivery/credentials', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ target: selectedTarget }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(
          payload.error ?? 'Failed to fetch delivery credentials',
        );
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const fileNameMatch = disposition.match(/filename="([^"]+)"/);
      const fileName =
        fileNameMatch?.[1] ?? `delivery-creds-${selectedTarget}.env`;
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      setDialogOpen(false);
    } catch (downloadError) {
      setError(
        downloadError instanceof Error
          ? downloadError.message
          : 'Failed to fetch delivery credentials',
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        color: '#0f172a',
        background:
          'radial-gradient(circle at 10% 10%, #dbeafe 0%, #f8fafc 40%, #f1f5f9 100%)',
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#0b3a78',
          backgroundImage: 'linear-gradient(90deg, #0b3a78 0%, #124c9e 100%)',
        }}
      >
        <Toolbar sx={{ height: 84 }}>
          <Box
            component="img"
            src="/simpl-logo.svg"
            alt="Simpl"
            sx={{ height: 64, maxHeight: '100%', width: 'auto' }}
          />
        </Toolbar>
      </AppBar>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          mt: 1.5,
          mb: 2.5,
          px: { xs: 3, sm: 4 },
          maxWidth: 1280,
          mx: 'auto',
          '@media (min-width:1536px)': { maxWidth: 1536 },
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 800, color: '#0b3a78', mb: 1 }}
        >
          Simpl OnDemand
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: '#334155', mb: 3, maxWidth: 760 }}
        >
          Simpl OnDemand control panel with three quick actions.
        </Typography>

        {deliveryFeatureEnabled ? (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
            {deliveryFeatureEnabled ? (
              <Button
                variant="outlined"
                onClick={openDialog}
                sx={{
                  borderColor: '#0b3a78',
                  color: '#0b3a78',
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Delivery creds
              </Button>
            ) : null}
          </Box>
        ) : null}

        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, minmax(180px, 1fr))',
            },
            gap: 2.5,
            maxWidth: 980,
          }}
        >
          {actions.map(action => (
            <Card
              key={action.id}
              variant="outlined"
              sx={{
                borderColor: action.borderColor,
                bgcolor: '#ffffff',
                boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
                transition: 'transform 160ms ease, box-shadow 160ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 14px 36px rgba(15,23,42,0.12)',
                },
              }}
            >
              <CardActionArea
                sx={{
                  p: 3,
                  minHeight: 120,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 1,
                  bgcolor: '#ffffff',
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={800}
                  sx={{ color: action.accentColor }}
                >
                  {action.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155' }}>
                  {action.description}
                </Typography>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        <Dialog
          open={dialogOpen}
          onClose={() => !downloading && setDialogOpen(false)}
          fullWidth
        >
          <DialogTitle>Download delivery credentials</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: '#475569', mb: 2 }}>
              This export contains sensitive platform and application
              credentials. Download only for approved operational use.
            </Typography>

            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}

            <FormControl fullWidth disabled={loadingTargets || downloading}>
              <InputLabel id="delivery-target-label">Target cluster</InputLabel>
              <Select
                labelId="delivery-target-label"
                value={selectedTarget}
                label="Target cluster"
                onChange={event =>
                  setSelectedTarget(String(event.target.value))
                }
              >
                {targets.map(target => (
                  <MenuItem key={target.id} value={target.id}>
                    {target.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {loadingTargets ? (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  Loading available targets...
                </Typography>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={downloading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDownload}
              disabled={!selectedTarget || loadingTargets || downloading}
              sx={{ textTransform: 'none' }}
            >
              {downloading ? 'Downloading...' : 'Download'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};
